import { isPage } from "../../../../common/utils";

/**
 * Pure helpers for the scene index multi-selection (Ctrl/Shift+click, bulk
 * delete, bulk drag). Only scenes (pages) take part in multi-selection —
 * sections are never selectable here.
 */

const hasCollapsedAncestor = (id, navItems) => {
  let parentId = navItems[id] && navItems[id].parent;
  while (parentId !== undefined && parentId !== null && parentId !== 0) {
    const parent = navItems[parentId];
    if (!parent) return false;
    if (parent.isExpanded === false) return true;
    parentId = parent.parent;
  }
  return false;
};

/** Scenes in on-screen order (scenes under a collapsed section are skipped). */
export function getSelectableSceneOrder(navItemsIds, navItems) {
  return navItemsIds.filter(
    (id) => isPage(id) && navItems[id] && !hasCollapsedAncestor(id, navItems)
  );
}

export function toggleSceneSelection(selectedIds, id) {
  return selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : selectedIds.concat(id);
}

/** Inclusive range between anchor and id in `sceneOrder`. */
export function rangeSceneSelection(sceneOrder, anchorId, id) {
  const from = sceneOrder.indexOf(anchorId);
  const to = sceneOrder.indexOf(id);
  if (from === -1 || to === -1) return to === -1 ? [] : [id];
  return sceneOrder.slice(Math.min(from, to), Math.max(from, to) + 1);
}

/** Drops ids that were deleted or are not scenes; keeps identity when unchanged. */
export function pruneSceneSelection(selectedIds, navItems) {
  const next = selectedIds.filter((id) => isPage(id) && navItems[id]);
  return next.length === selectedIds.length ? selectedIds : next;
}

export function pagesLeftAfterDelete(selectedIds, navItems) {
  return Object.keys(navItems).filter((id) => isPage(id) && !selectedIds.includes(id)).length;
}

export function sortByTreeOrder(ids, navItemsIds) {
  return navItemsIds.filter((id) => ids.includes(id));
}

const parentFromPath = (path) => (path && path.length ? path[path.length - 1] : 0);

/**
 * Turns a single-item Sortly drop into a block move of the whole selection.
 *
 * `items` is the Sortly list after the drop (only `movedId` changed place).
 * The other selected scenes are pulled out and re-inserted around `movedId`,
 * keeping their original relative order, under the moved scene's new parent.
 *
 * Returns one reorder op per scene (moved scene first), each carrying the final
 * `idsInOrder` / `childrenInOrder`, so dispatching them in sequence through the
 * existing REORDER_NAV_ITEM reducer converges on the final tree. Returns null
 * when the drop is not a multi-selection move.
 */
export function buildGroupMoveOps(items, movedId, selectedIds, navItems, navItemsIds) {
  if (!selectedIds.includes(movedId) || selectedIds.length < 2) return null;

  const moved = items.find((item) => item.id === movedId);
  if (!moved) return null;
  const newParent = parentFromPath(moved.path);

  const block = sortByTreeOrder(selectedIds, navItemsIds);
  const others = block.filter((id) => id !== movedId);
  if (others.length === 0) return null;

  const remaining = items.filter((item) => !others.includes(item.id));
  const idsInOrder = [];
  remaining.forEach((item) => {
    if (item.id === movedId) idsInOrder.push(...block);
    else idsInOrder.push(item.id);
  });

  const pathById = {};
  items.forEach((item) => {
    pathById[item.id] = item.path;
  });
  const childrenInOrder = idsInOrder.filter((id) =>
    block.includes(id) ? true : parentFromPath(pathById[id]) === newParent
  );

  return [movedId, ...others].map((id) => ({
    id,
    newParent,
    oldParent: navItems[id].parent,
    idsInOrder,
    childrenInOrder,
  }));
}
